import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { RoleService } from '../../lib/api'
import {
	CreateRoleDataProps,
	GetRoleUsersDataResponse,
} from '../../lib/api/types/role.interfaces'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import toastCaller from '../../lib/utils/toastCaller'
import { handleApiError } from '../../lib/utils/handleApiError'

export const useCreateRole = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['roleCreate'],
		mutationFn: ({
			data,
		}: {
			data: CreateRoleDataProps
		}) => RoleService.create({ data}),
		onError: (error, variables) => {
			const rawName = variables?.data?.name
			const readableName = rawName
				? rawName
						.replace(/_/g, ' ')
						.replace(/\s+/g, ' ')
						.trim()
						.replace(/\b\w/g, (c) => c.toUpperCase())
				: undefined

			handleApiError(error, readableName)
		},
		// onError: (error: any) => {
		// 	toastCaller({
		// 		type: 'error',
		// 		message: 'Error creating role',
		// 		errorMessageCode: error?.response?.data?.error,
		// 	})
		// },
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationRoles'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error: any) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRenameRole = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['roleRename'],
		mutationFn: ({
			name,
			id,
		}: {
			name: string
			id: string
		}) => RoleService.rename({ name, id}),
		onError: (error, variables) => {
			const rawName = variables?.name
			const readableName = rawName
				? rawName
						.replace(/_/g, ' ')
						.replace(/\s+/g, ' ')
						.trim()
						.replace(/\b\w/g, (c) => c.toUpperCase())
				: undefined

			handleApiError(error, readableName)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationRoles'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error: any) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteRole = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['rolesDelete'],
		mutationFn: ({ id }: DefaultGetParamsProps) =>
			RoleService.delete({ id}),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationRoles'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateRoleUsers = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['roleAddUsers'],
		mutationFn: ({
			usersIds,
			id,
		}: {
			usersIds: { id: string }[]
			id: string
		}) => RoleService.updateUsers({ usersIds, id}),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['roleUsers'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useAddUserToRole = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['roleAddUser'],
		mutationFn: ({
			userId,
			id,
			invalidate,
		}: {
			userId: string
			id: string
			invalidate?: boolean
		}) => RoleService.addUser({ userId, id}),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['roleUsers'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteUserFromRole = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['roleDeleteUser'],
		mutationFn: ({
			userId,
			id,
			invalidate,
		}: {
			userId: string
			id: string
			invalidate?: boolean
		}) => RoleService.deleteUser({ userId, id}),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate = true }) => {
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['roleUsers'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRoleUsers = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetRoleUsersDataResponse>>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery({
		queryKey: ['roleUsers', id],
		queryFn: () => RoleService.getUsers({ id}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}
